import { useCallback, useEffect, useRef, useState } from 'react'
import { intl } from '../../../../i18n'
import store from '../../../../store'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import {
  fetchProgramList,
  fetchProgramManagementData,
} from '../../../../infrastructure/api/program-management'
import { AgGridTreeHelper } from '../../../containers/BulkSheetView/lib/tree'
import {
  Program,
  ProgramManagementResponse,
  ProgramManagementRow,
} from '../programManagement'
import { aggregateTreeData } from './aggregateTreeData'

const mapResponseToRow = (
  source: ProgramManagementResponse
): ProgramManagementRow => ({
  uuid: source.uuid,
  lockVersion: source.lockVersion,
  treeValue: [],
  type: source.type,
  iconUrl: source.iconUrl,
  code: source.code,
  displayName: source.displayName,
  status: source.status,
  projectManager: source.projectManager,
  scheduledDate: source.scheduledDate,
  progress: source.progress,
  resource: source.resource,
  profitLoss: source.profitLoss,
})

export type UseProgramManagementDataReturn = {
  data: ProgramManagementRow[]
  loading: boolean
  programs: Program[]
  reload: () => Promise<void>
}

export const useProgramManagementData = (
  selectedProgramUuid: string | undefined,
  setSelectedProgramUuid: (uuid: string | undefined) => void,
  code?: string
): UseProgramManagementDataReturn => {
  const [data, setData] = useState<ProgramManagementRow[]>([])
  const [loading, setLoading] = useState(false)
  const [programs, setPrograms] = useState<Program[]>([])
  const originalDataRef = useRef<ProgramManagementResponse | undefined>(
    undefined
  )
  const programsLoadedRef = useRef(false)

  // マウント時にプログラム一覧を取得
  useEffect(() => {
    const loadPrograms = async () => {
      const programList = await fetchProgramList()
      setPrograms(programList)
      programsLoadedRef.current = true

      if (code) {
        const matched = programList.find(p => p.code === code)
        if (matched) {
          setSelectedProgramUuid(matched.uuid)
        } else {
          store.dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              text: intl.formatMessage({
                id: 'programManagement.programNotFound',
              }),
            })
          )
        }
      }
    }
    loadPrograms()
  }, [code, setSelectedProgramUuid])

  const fetchData = useCallback(async (programUuid: string) => {
    setLoading(true)
    try {
      const response = await fetchProgramManagementData(programUuid)
      originalDataRef.current = response
      const rows = AgGridTreeHelper.convert(response, mapResponseToRow)
      const aggregated = aggregateTreeData(rows)
      setData(aggregated)
    } finally {
      setLoading(false)
    }
  }, [])

  // プログラム選択時にデータを取得
  useEffect(() => {
    if (!selectedProgramUuid) return
    fetchData(selectedProgramUuid)
  }, [selectedProgramUuid, fetchData])

  const reload = useCallback(async () => {
    if (!selectedProgramUuid) return
    await fetchData(selectedProgramUuid)
  }, [selectedProgramUuid, fetchData])

  return {
    data,
    loading,
    programs,
    reload,
  }
}
